import { Box, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import SvgToIcon from "../../../../../../icons/SvgToIcon";
import IntegrationImage from "../../../../integrations/components/IntegrationImage";
import { icons } from "../../../icons/iconExport";
import { DataStoreResourceTypeIconsAndLabels } from "../../../utils";
import TextIntegrationStyle from "../../TextIntegrationStyle";

import useAPI from "../../../../../../api/hooks/useAPI";
import { ComplexEntityTypes } from "../../../../../../utils/constants";
import { useEntities } from "../../../hooks/useEntities";
import EntityDrawer from "../../EntityDrawer/EntityDrawer";

const StarburstSnowflakeCatalogDetailsView = ({ entityData }) => {
  const [database_logo, database_label] = Object.keys(
    DataStoreResourceTypeIconsAndLabels
  ).includes(entityData.resource_type)
    ? DataStoreResourceTypeIconsAndLabels[entityData.resource_type]
    : null;

  const [isUserDrawerOpen, setIsUserDrawerOpen] = useState(false);
  const [isDataStoreDrawerOpen, setIsDataStoreDrawerOpen] = useState(false);

  const { useQueryForEntitiesByType } = useEntities();

  const { data: userEntities } = useQueryForEntitiesByType({
    type: ComplexEntityTypes.USER,
  });

  const { data: nonHumanEntities } = useQueryForEntitiesByType({
    type: ComplexEntityTypes.NONHUMAN_IDENTITY,
  });

  const { data: dataStoreEntities } = useQueryForEntitiesByType({
    type: ComplexEntityTypes.DATA_STORE,
  });

  const { trackEvent } = useAPI();

  // Find the user entity that matches the user_name
  const findUserEntity = () => {
    if (
      !userEntities ||
      !entityData?.starburst_snowflake_catalog_data?.user_name
    ) {
      return null;
    }

    return userEntities.find(
      (entity) =>
        entity?.snowflake_data?.login_names?.length > 0 &&
        entity?.snowflake_data?.login_names.includes(
          entityData.starburst_snowflake_catalog_data.user_name.toUpperCase()
        )
    );
  };

  // Find the non human entity that matches the user_name
  const findNonHumanEntity = () => {
    if (
      !nonHumanEntities ||
      !entityData?.starburst_snowflake_catalog_data?.user_name
    ) {
      return null;
    }

    return nonHumanEntities.find(
      (entity) =>
        entity?.id &&
        entity?.id ===
          entityData.starburst_snowflake_catalog_data.user_name.toUpperCase()
    );
  };

  const findDataStoreEntity = () => {
    if (
      !dataStoreEntities ||
      !entityData?.starburst_snowflake_catalog_data?.database_name
    ) {
      return null;
    }

    return dataStoreEntities.find(
      (entity) =>
        entity?.snowflake_data?.name &&
        entity?.snowflake_data?.name ===
          entityData.starburst_snowflake_catalog_data.database_name.toUpperCase()
    );
  };

  // Update connected user entity when user entities or entity data changes
  const snowflakeUserEntity = useMemo(() => {
    if (
      userEntities &&
      entityData?.starburst_snowflake_catalog_data?.user_name
    ) {
      const userEntity = findUserEntity();
      return userEntity;
    }
    return null;
  }, [userEntities, entityData?.starburst_snowflake_catalog_data?.user_name]);

  const snowflakeNonHumanEntity = useMemo(() => {
    if (
      nonHumanEntities &&
      entityData?.starburst_snowflake_catalog_data?.user_name
    ) {
      const nonHumanEntity = findNonHumanEntity();
      return nonHumanEntity;
    }
    return null;
  }, [
    nonHumanEntities,
    entityData?.starburst_snowflake_catalog_data?.user_name,
  ]);

  const snowflakeDataStoreEntity = useMemo(() => {
    if (
      dataStoreEntities &&
      entityData?.starburst_snowflake_catalog_data?.database_name
    ) {
      const dataStoreEntity = findDataStoreEntity();
      return dataStoreEntity;
    }
    return null;
  }, [
    dataStoreEntities,
    entityData?.starburst_snowflake_catalog_data?.database_name,
  ]);

  const onUserEmailClick = () => {
    trackEvent(
      `User clicked on user ${entityData?.starburst_snowflake_catalog_data?.user_name}, from starburst snowflake catalog details view ${entityData?.display_name}`
    );

    if (snowflakeUserEntity || snowflakeNonHumanEntity) {
      setIsUserDrawerOpen(true);
    }
  };

  const onDataStoreClick = () => {
    trackEvent(
      `User clicked on data store ${entityData?.starburst_snowflake_catalog_data?.database_name}, from starburst snowflake catalog details view ${entityData?.display_name}`
    );

    if (snowflakeDataStoreEntity) {
      setIsDataStoreDrawerOpen(true);
    }
  };

  // Get the display name for the user
  const getUserDisplayName = () => {
    if (snowflakeUserEntity) {
      return (
        snowflakeUserEntity.display_name ||
        snowflakeUserEntity.original_unique_id
      );
    }
    if (snowflakeNonHumanEntity) {
      return snowflakeNonHumanEntity.display_name;
    }
    return entityData?.starburst_snowflake_catalog_data?.user_name;
  };

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Box display="flex" flexDirection="column" gap={1}>
        <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
          <SvgToIcon IconLogo={icons.catalogIcon} width="16px" />
          <Typography variant="subtitle2">Catalog Name</Typography>
        </Box>
        <Box ml={2} display="flex">
          <TextIntegrationStyle
            title=""
            value={entityData.display_name}
          ></TextIntegrationStyle>
        </Box>
      </Box>
      {entityData?.starburst_snowflake_catalog_data?.database_name && (
        <Box display="flex" flexDirection="column" gap={1}>
          <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
            <SvgToIcon IconLogo={icons.databaseIcon} width="16px" />
            <Typography variant="subtitle2">Connected Database</Typography>
          </Box>
          <Box ml={2} display="flex">
            <IntegrationImage
              name={"snowflake"}
              width={"16px"}
              newLogo={true}
            />
            <TextIntegrationStyle
              title={""}
              value={entityData.starburst_snowflake_catalog_data.database_name}
              onClick={snowflakeDataStoreEntity ? onDataStoreClick : undefined}
              style={
                snowflakeDataStoreEntity
                  ? {
                      cursor: "pointer",
                      textDecoration: "underline",
                    }
                  : {}
              }
              tooltipTitle={
                snowflakeDataStoreEntity
                  ? "Click to view data store details"
                  : undefined
              }
            ></TextIntegrationStyle>
          </Box>
        </Box>
      )}
      {entityData?.starburst_snowflake_catalog_data?.user_name && (
        <Box display="flex" flexDirection="column" gap={1}>
          <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
            <SvgToIcon IconLogo={icons.userIcon} width="16px" />
            <Typography variant="subtitle2">Integration User</Typography>
          </Box>
          <Box ml={2} display="flex">
            <IntegrationImage
              name={"snowflake"}
              width={"16px"}
              newLogo={true}
            />
            <TextIntegrationStyle
              title={""}
              value={getUserDisplayName()}
              onClick={
                snowflakeUserEntity || snowflakeNonHumanEntity
                  ? onUserEmailClick
                  : undefined
              }
              style={
                snowflakeUserEntity || snowflakeNonHumanEntity
                  ? {
                      cursor: "pointer",
                      textDecoration: "underline",
                    }
                  : {}
              }
              tooltipTitle={
                snowflakeUserEntity ? "Click to view user details" : undefined
              }
            ></TextIntegrationStyle>
          </Box>
        </Box>
      )}
      {entityData?.starburst_snowflake_catalog_data?.authentication_type && (
        <Box display="flex" flexDirection="column" gap={1}>
          <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
            <SvgToIcon IconLogo={icons.authenticationIcon} width="16px" />
            <Typography variant="subtitle2">Authentication Type</Typography>
          </Box>
          <Box ml={2} display="flex">
            <TextIntegrationStyle
              title=""
              value={
                entityData.starburst_snowflake_catalog_data.authentication_type
              }
            ></TextIntegrationStyle>
          </Box>
        </Box>
      )}
      {entityData?.starburst_snowflake_catalog_data?.read_only !==
        undefined && (
        <Box display="flex" flexDirection="column" gap={1}>
          <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
            <SvgToIcon IconLogo={icons.readOnlyIcon} width="16px" />
            <Typography variant="subtitle2">Read Only</Typography>
          </Box>
          <Box ml={2} display="flex">
            <TextIntegrationStyle
              title=""
              value={
                entityData.starburst_snowflake_catalog_data.read_only
                  ? "On"
                  : "Off"
              }
            ></TextIntegrationStyle>
          </Box>
        </Box>
      )}
      {entityData?.starburst_snowflake_catalog_data?.account_identifier && (
        <Box display="flex" flexDirection="column" gap={1}>
          <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
            <SvgToIcon IconLogo={icons.serialNumberIcon} width="16px" />
            <Typography variant="subtitle2">Account Identifier</Typography>
          </Box>
          <Box ml={2} display="flex">
            <IntegrationImage
              name={"snowflake"}
              width={"16px"}
              newLogo={true}
            />
            <TextIntegrationStyle
              title=""
              value={
                entityData.starburst_snowflake_catalog_data.account_identifier
              }
            ></TextIntegrationStyle>
          </Box>
        </Box>
      )}
      <EntityDrawer
        openDrawer={
          isUserDrawerOpen && (snowflakeUserEntity || snowflakeNonHumanEntity)
        }
        selectedEntity={snowflakeUserEntity || snowflakeNonHumanEntity}
        onClose={() => {
          setIsUserDrawerOpen(false);
        }}
      />
      <EntityDrawer
        openDrawer={isDataStoreDrawerOpen && snowflakeDataStoreEntity}
        selectedEntity={snowflakeDataStoreEntity}
        onClose={() => {
          setIsDataStoreDrawerOpen(false);
        }}
      />
    </Box>
  );
};

export default StarburstSnowflakeCatalogDetailsView;
